import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ContactMessages.module.css";
import Table from "../../components/Table";
import { api, API_BASE } from "../../lib/api";
import { getFriendlyMessage } from "../../utils/errorMapping";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso || "";
  }
}

function clip(s, n = 120) {
  const t = String(s || "");
  if (t.length <= n) return t;
  return t.slice(0, n) + "…";
}

function getSourceBadge(source) {
  switch (source) {
    case "app_name_consultation":
      return { label: "✨ Name Consultation", className: styles.badgeName };
    case "app_mobile_consultation":
      return { label: "📱 Mobile Consultation", className: styles.badgeMobile };
    case "contact_page":
    default:
      return { label: "🌐 Web Inquiries", className: styles.badgeWeb };
  }
}

function getCleanPhone(phone) {
  return String(phone || "").replace(/[^\d]/g, "");
}

function buildWhatsAppUrl(phone, clientName, subject) {
  const clean = getCleanPhone(phone);
  if (!clean) return "#";
  const p = clean.length === 10 ? `91${clean}` : clean;
  const greeting = encodeURIComponent(
    `Namaste ${clientName || "Ji"} 🙏\n\nThank you for reaching out to Astro School Baba (ASB Numerology) regarding ${subject || "your consultation request"}.\n\nOur senior numerologist is ready to assist you. How can we help you today?`
  );
  return `https://wa.me/${p}?text=${greeting}`;
}

async function downloadExcel(url) {
  const token = localStorage.getItem("asb_access_token") || "";

  const res = await fetch(url, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || "Export failed");
  }

  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="(.+?)"/i);
  const filename = match?.[1] || "consultation-leads.xlsx";

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(link.href);
}

export default function ContactMessages() {
  const [status, setStatus] = useState("ALL"); // ALL | NEW | CONTACTED | READ
  const [source, setSource] = useState("ALL"); // ALL | app_name_consultation | app_mobile_consultation | contact_page
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });

  const [viewing, setViewing] = useState(null); // message object for modal

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/api/admin/contact-messages", {
        query: {
          page,
          limit,
          status: status === "ALL" ? undefined : status,
          source: source === "ALL" ? undefined : source,
          search: search.trim() || undefined,
        },
      });

      setData({
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total || 0),
        pages: Number(res?.pages || 1),
      });
    } catch (e) {
      setErr(getFriendlyMessage(e));
      setData({ items: [], total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, source, search]);

  useEffect(() => {
    load();
  }, [load]);

  const onExport = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (status !== "ALL") params.append("status", status);
      if (source !== "ALL") params.append("source", source);
      const q = params.toString() ? `?${params.toString()}` : "";
      const url = `${API_BASE}/api/admin/contact-messages/export${q}`;
      await downloadExcel(url);
    } catch (e) {
      setErr(getFriendlyMessage(e));
    }
  }, [status, source]);

  const updateStatus = useCallback(
    async (id, newStatus) => {
      if (!id) return;

      // optimistic update
      setData((prev) => ({
        ...prev,
        items: prev.items.map((x) => (x._id === id ? { ...x, status: newStatus } : x)),
      }));

      try {
        await api.patch(`/api/admin/contact-messages/${id}`, { status: newStatus });
        if (viewing && viewing._id === id) {
          setViewing((prev) => (prev ? { ...prev, status: newStatus } : null));
        }
      } catch (e) {
        setErr(getFriendlyMessage(e));
        await load();
      }
    },
    [load, viewing]
  );

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
    } catch {
      // ignore
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        key: "source",
        title: "Type",
        render: (r) => {
          const badge = getSourceBadge(r.source);
          return <span className={`${styles.badge} ${badge.className}`}>{badge.label}</span>;
        },
      },
      {
        key: "status",
        title: "Status",
        render: (r) => {
          const st = r.status || "NEW";
          const cls =
            st === "NEW" ? styles.badgeNew : st === "CONTACTED" ? styles.badgeContacted : styles.badgeRead;
          return <span className={`${styles.badge} ${cls}`}>{st}</span>;
        },
      },
      {
        key: "client",
        title: "Client",
        render: (r) => (
          <div className={styles.cellStack}>
            <div className={styles.clientName}>{r.name || "App User"}</div>
            <div className={styles.clientMetaRow}>
              <a className={styles.cellLink} href={`tel:${r.phone || ""}`}>
                📞 {r.phone || "-"}
              </a>
              <button
                className={styles.miniBtn}
                type="button"
                title="Copy phone"
                onClick={() => copyToClipboard(r.phone)}
              >
                Copy
              </button>
            </div>
            {r.email && (
              <div className={styles.clientEmail}>
                ✉️ {r.email}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "message",
        title: "Lead Details / Context",
        render: (r) => (
          <div className={styles.msgCell}>
            <div className={styles.msgPreview}>{clip(r.message, 150)}</div>
            <button className={styles.viewDetailsBtn} type="button" onClick={() => setViewing(r)}>
              🔍 View Full Analysis
            </button>
          </div>
        ),
      },
      {
        key: "createdAt",
        title: "Date",
        render: (r) => <span className={styles.muted}>{fmtDate(r.createdAt)}</span>,
      },
      {
        key: "actions",
        title: "Quick Action",
        render: (r) => {
          const waUrl = buildWhatsAppUrl(r.phone, r.name, r.source?.includes("name") ? "Name Correction" : "Mobile Consultation");
          return (
            <div className={styles.actions}>
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.whatsAppBtn}
                title="Open WhatsApp Chat"
              >
                💬 Chat on WhatsApp
              </a>

              {r.status === "NEW" ? (
                <button
                  className={styles.markBtn}
                  type="button"
                  onClick={() => updateStatus(r._id, "CONTACTED")}
                >
                  Mark Contacted
                </button>
              ) : r.status === "CONTACTED" ? (
                <button
                  className={styles.markBtn}
                  type="button"
                  onClick={() => updateStatus(r._id, "READ")}
                >
                  Mark Read
                </button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [copyToClipboard, updateStatus]
  );

  const canPrev = page > 1;
  const canNext = page < (data.pages || 1);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.h1}>📋 Consultation Leads & Form Submissions</h1>
          <div className={styles.sub}>
            View and respond to incoming Name & Mobile consultation requests directly via WhatsApp or Phone.
          </div>
        </div>

        <div className={styles.headerRight}>
          <button className={styles.refreshBtn} type="button" onClick={load} disabled={loading}>
            {loading ? "Refreshing..." : "🔄 Refresh"}
          </button>
          <button className={styles.exportBtn} type="button" onClick={onExport} disabled={loading}>
            📥 Export to Excel
          </button>
        </div>
      </div>

      {/* Source & Search Filter Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.sourceTabs}>
          <button
            type="button"
            className={`${styles.tabBtn} ${source === "ALL" ? styles.tabActive : ""}`}
            onClick={() => {
              setPage(1);
              setSource("ALL");
            }}
          >
            All Leads
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${source === "app_name_consultation" ? styles.tabActive : ""}`}
            onClick={() => {
              setPage(1);
              setSource("app_name_consultation");
            }}
          >
            ✨ Name Consultations
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${source === "app_mobile_consultation" ? styles.tabActive : ""}`}
            onClick={() => {
              setPage(1);
              setSource("app_mobile_consultation");
            }}
          >
            📱 Mobile Consultations
          </button>
          <button
            type="button"
            className={`${styles.tabBtn} ${source === "contact_page" ? styles.tabActive : ""}`}
            onClick={() => {
              setPage(1);
              setSource("contact_page");
            }}
          >
            🌐 Web Inquiries
          </button>
        </div>

        <div className={styles.searchBox}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="🔍 Search client name or phone..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Status Filter Bar */}
      <div className={styles.statusToolbar}>
        <div className={styles.filters}>
          <button
            type="button"
            className={`${styles.filterBtn} ${status === "ALL" ? styles.filterActive : ""}`}
            onClick={() => {
              setPage(1);
              setStatus("ALL");
            }}
          >
            All Statuses
          </button>
          <button
            type="button"
            className={`${styles.filterBtn} ${status === "NEW" ? styles.filterActive : ""}`}
            onClick={() => {
              setPage(1);
              setStatus("NEW");
            }}
          >
            🔴 New Leads
          </button>
          <button
            type="button"
            className={`${styles.filterBtn} ${status === "CONTACTED" ? styles.filterActive : ""}`}
            onClick={() => {
              setPage(1);
              setStatus("CONTACTED");
            }}
          >
            🔵 Contacted
          </button>
          <button
            type="button"
            className={`${styles.filterBtn} ${status === "READ" ? styles.filterActive : ""}`}
            onClick={() => {
              setPage(1);
              setStatus("READ");
            }}
          >
            ✅ Read / Closed
          </button>
        </div>

        <div className={styles.pager}>
          <span className={styles.muted}>
            Total: <b className={styles.strong}>{data.total}</b>
          </span>

          <div className={styles.pagerBtns}>
            <button
              className={styles.pagerBtn}
              type="button"
              disabled={!canPrev || loading}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span className={styles.pageNum}>
              Page <b className={styles.strong}>{page}</b> / {data.pages || 1}
            </span>
            <button
              className={styles.pagerBtn}
              type="button"
              disabled={!canNext || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className={styles.errorBox}>
          <div className={styles.errorTitle}>Couldn’t load leads</div>
          <div className={styles.errorMsg}>{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className={styles.state}>Loading consultation leads…</div>
      ) : (
        <Table columns={columns} rows={data.items} emptyText="No consultation leads found." />
      )}

      {/* Detailed Lead Modal */}
      {viewing ? (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-label="Lead details">
          <div className={styles.modalCard}>
            <div className={styles.modalHead}>
              <div>
                <div className={styles.modalBadgeRow}>
                  {(() => {
                    const badge = getSourceBadge(viewing.source);
                    return <span className={`${styles.badge} ${badge.className}`}>{badge.label}</span>;
                  })()}
                  <span
                    className={`${styles.badge} ${
                      viewing.status === "NEW"
                        ? styles.badgeNew
                        : viewing.status === "CONTACTED"
                        ? styles.badgeContacted
                        : styles.badgeRead
                    }`}
                  >
                    {viewing.status || "NEW"}
                  </span>
                </div>
                <div className={styles.modalTitle}>{viewing.name || "Client Lead"}</div>
                <div className={styles.modalMeta}>
                  <span>📞 {viewing.phone || "-"}</span>
                  <span className={styles.dot}>•</span>
                  <span>✉️ {viewing.email || "-"}</span>
                  <span className={styles.dot}>•</span>
                  <span>📅 {fmtDate(viewing.createdAt)}</span>
                </div>
              </div>

              <button className={styles.closeBtn} type="button" onClick={() => setViewing(null)} aria-label="Close">
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalLabel}>Numerology Breakdown & Request Details</div>
              <div className={styles.modalMsg}>{viewing.message}</div>
            </div>

            <div className={styles.modalActions}>
              <a
                href={buildWhatsAppUrl(
                  viewing.phone,
                  viewing.name,
                  viewing.source?.includes("name") ? "Name Correction" : "Mobile Consultation"
                )}
                target="_blank"
                rel="noreferrer"
                className={styles.whatsAppBtnLarge}
              >
                💬 Chat with Client on WhatsApp
              </a>

              {viewing.status !== "CONTACTED" && (
                <button
                  className={styles.markContactedBtn}
                  type="button"
                  onClick={() => updateStatus(viewing._id, "CONTACTED")}
                >
                  Mark as Contacted
                </button>
              )}

              {viewing.status !== "READ" && (
                <button
                  className={styles.markReadBtn}
                  type="button"
                  onClick={() => updateStatus(viewing._id, "READ")}
                >
                  Mark as Read
                </button>
              )}

              <button className={styles.modalCloseBtn} type="button" onClick={() => setViewing(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
